import { useState } from "react";

import Eyebrow from "../shared/Eyebrow";
import TypedHeading from "../shared/TypedHeading";
import Reveal from "../shared/Reveal";
import useI18n from "../../i18n/useI18n";
import { trackEvent } from "../../utils/analytics";
import { submitContactLead } from "../../utils/contactApi";

const MIN_MESSAGE_LENGTH = 20;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const initialValues = { fullName: "", email: "", phone: "", message: "" };

const ContactSection = () => {
  const { t } = useI18n();
  const [values, setValues] = useState(initialValues);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const validate = (vals) => {
    const next = {};
    if (!vals.fullName.trim()) next.fullName = t("contact.errName");
    if (!vals.email.trim()) next.email = t("contact.errEmailRequired");
    else if (!EMAIL_RE.test(vals.email.trim())) next.email = t("contact.errEmailInvalid");
    if (!vals.message.trim()) next.message = t("contact.errMessageRequired");
    else if (vals.message.trim().length < MIN_MESSAGE_LENGTH) next.message = t("contact.errMessageShort");
    return next;
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (status === "error") setStatus("idle");
    if (submitError) setSubmitError("");
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    trackEvent("form_start", { source: "contact_section" });
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus("error");
      trackEvent("form_error", { source: "contact_section", error_fields: Object.keys(nextErrors) });
      return;
    }

    setStatus("submitting");
    setSubmitError("");
    try {
      await submitContactLead({
        FullName: values.fullName.trim(),
        Email: values.email.trim(),
        Phone: values.phone.trim(),
        ProjectType: "General Inquiry",
        Message: values.message.trim(),
      });
      setValues(initialValues);
      setErrors({});
      setStatus("success");
      trackEvent("form_success", { source: "contact_section" });
    } catch (error) {
      setStatus("error");
      setSubmitError(
        error instanceof Error && error.message.trim() ? error.message : t("contact.errSubmit")
      );
      trackEvent("form_error", { source: "contact_section", error_fields: ["submission"] });
    }
  };

  return (
    <section id="contact" className="contact section">
      <div className="contact__inner">
        <div className="contact__copy">
          <Reveal><Eyebrow>{t("contact.eyebrow")}</Eyebrow></Reveal>
          <TypedHeading text={t("contact.title")} className="contact__title" />
          <Reveal as="p" className="contact__lede">{t("contact.lede")}</Reveal>
          <Reveal>
            <a className="contact__email" href={`mailto:${t("contact.email")}`}>
              {t("contact.email")}
            </a>
            <a
              className="contact__phone"
              href={`tel:+972${t("contact.phone").replace(/\D/g, "").replace(/^0/, "")}`}
              dir="ltr"
            >
              {t("contact.phone")}
            </a>
          </Reveal>
        </div>

        <Reveal className="contact__form-wrap">
          {status === "success" ? (
            <div className="contact__success" role="status" aria-live="polite">
              <h3>{t("contact.successTitle")}</h3>
              <p>{t("contact.successBody")}</p>
            </div>
          ) : (
            <form className="contact-form" onSubmit={onSubmit} noValidate aria-busy={status === "submitting"}>
              <div className="contact-form__row">
                <label htmlFor="c-name" className="visually-hidden">{t("contact.namePlaceholder")}</label>
                <input
                  id="c-name"
                  name="fullName"
                  className="contact-form__input"
                  placeholder={t("contact.namePlaceholder")}
                  value={values.fullName}
                  onChange={onChange}
                />
                {errors.fullName && <p className="contact-form__err">{errors.fullName}</p>}
              </div>

              <div className="contact-form__row">
                <label htmlFor="c-email" className="visually-hidden">{t("contact.emailPlaceholder")}</label>
                <input
                  id="c-email"
                  name="email"
                  type="email"
                  className="contact-form__input"
                  placeholder={t("contact.emailPlaceholder")}
                  value={values.email}
                  onChange={onChange}
                />
                {errors.email && <p className="contact-form__err">{errors.email}</p>}
              </div>

              <div className="contact-form__row">
                <label htmlFor="c-phone" className="visually-hidden">{t("contact.phonePlaceholder")}</label>
                <input
                  id="c-phone"
                  name="phone"
                  type="tel"
                  className="contact-form__input"
                  placeholder={t("contact.phonePlaceholder")}
                  value={values.phone}
                  onChange={onChange}
                />
              </div>

              <div className="contact-form__row">
                <label htmlFor="c-message" className="visually-hidden">{t("contact.messagePlaceholder")}</label>
                <textarea
                  id="c-message"
                  name="message"
                  rows={4}
                  className="contact-form__input contact-form__textarea"
                  placeholder={t("contact.messagePlaceholder")}
                  value={values.message}
                  onChange={onChange}
                />
                {errors.message && <p className="contact-form__err">{errors.message}</p>}
              </div>

              <button type="submit" className="contact-form__submit" disabled={status === "submitting"}>
                {status === "submitting" ? t("contact.submitting") : t("contact.submit")}
              </button>

              {status === "error" && submitError && (
                <p className="contact-form__submit-err" role="alert">{submitError}</p>
              )}
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default ContactSection;
